import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "../components/site/Seo";
import Reveal from "../components/site/Reveal";
import { SectionLabel, ArrowLink, PrimaryButton, PlaceholderNote } from "../components/site/Bits";
import ClosingCTA from "../components/home/ClosingCTA";
import { fetchProject, fetchProjects } from "../lib/api";

const Meta = ({ label, value }) => (
  <div className="py-5" style={{ borderTop: "1px solid rgba(23,21,34,0.10)" }}>
    <span className="eyebrow block">{label}</span>
    <span className="mt-2 block text-[15px]">{value || "—"}</span>
  </div>
);

export default function WorkDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [others, setOthers] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    setState("loading");
    fetchProject(slug)
      .then((data) => {
        if (!alive) return;
        setProject(data);
        setState("ready");
      })
      .catch(() => alive && setState("error"));
    fetchProjects()
      .then((data) => alive && setOthers(data.filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => setOthers([]));
    return () => {
      alive = false;
    };
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="lk-container-wide" style={{ paddingTop: 200, paddingBottom: 160 }}>
        <div className="h-6 w-40 animate-pulse" style={{ background: "var(--lykit-lavender)" }} />
        <div className="mt-8 h-14 w-full max-w-[600px] animate-pulse" style={{ background: "var(--lykit-lavender)" }} />
        <div className="mt-10 w-full animate-pulse" style={{ aspectRatio: "21 / 9", background: "var(--lykit-lavender)" }} />
      </div>
    );
  }

  if (state === "error" || !project) {
    return (
      <div className="lk-container-wide" style={{ paddingTop: 200, paddingBottom: 160 }} data-testid="work-detail-error">
        <SectionLabel>Project</SectionLabel>
        <h1 className="display-lg mt-7">PROJECT NOT FOUND.</h1>
        <p className="mt-6 lead measure">
          This project may have been moved or is not published yet. Browse the full list of work instead.
        </p>
        <div className="mt-10">
          <PrimaryButton to="/work">Back to Our Work</PrimaryButton>
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo
        title={`${project.title} | LYKIT Entertainment`}
        description={project.summary || "Project by LYKIT Entertainment Pvt. Ltd."}
        path={`/work/${project.slug}`}
      />

      <section className="relative isolate overflow-hidden bg-nearblack" data-testid="work-detail-hero">
        <div className="absolute inset-0 z-0">
          <img src={project.image} alt={project.title} className="h-full w-full object-cover" loading="eager" />
        </div>
        <div
          className="grain absolute inset-0 z-10"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(14,13,20,0.8) 0%, rgba(14,13,20,0.55) 45%, rgba(14,13,20,0.9) 100%)",
          }}
        />
        <div className="relative z-20 pb-[clamp(34px,3.6vw,58px)] pt-[clamp(120px,13vh,160px)]">
          <div className="lk-container-wide">
            <Link
              to="/work"
              data-testid="work-detail-back"
              className="inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase transition-colors duration-200"
              style={{ letterSpacing: "0.16em", color: "rgba(247,246,242,0.7)" }}
            >
              <ArrowLeft size={14} strokeWidth={1.8} aria-hidden="true" /> All Work
            </Link>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <span
                className="rounded-full border px-4 py-[7px] text-[10.5px] font-semibold uppercase"
                style={{
                  letterSpacing: "0.16em",
                  borderColor: "rgba(216,183,122,0.6)",
                  color: "rgba(216,183,122,0.95)",
                }}
              >
                {project.category}
              </span>
              <span className="text-[12.5px]" style={{ color: "rgba(247,246,242,0.6)" }}>
                {project.year}
              </span>
            </div>
            <h1 className="display-xl mt-6 max-w-[22ch]" style={{ color: "#FBFAF7" }} data-testid="work-detail-title">
              {project.title}
            </h1>
          </div>
        </div>
      </section>

      <section className="lk-section">
        <div className="lk-container-wide">
          <div className="grid gap-y-12 lg:grid-cols-12 lg:gap-x-14">
            <div className="lg:col-span-7">
              <Reveal>
                <SectionLabel>Overview</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="display-md mt-7" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {project.summary}
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <p className="mt-8 lead measure">{project.description}</p>
              </Reveal>

              {project.is_placeholder && (
                <Reveal delay={0.2} className="mt-10">
                  <PlaceholderNote>
                    Placeholder project record — final case-study content pending from LYKIT
                  </PlaceholderNote>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-4 lg:col-start-9">
              <Reveal delay={0.1}>
                <Meta label="Category" value={project.category} />
                <Meta label="Client / Partner" value={project.client} />
                <Meta label="Year" value={project.year} />
              </Reveal>

              {project.scope?.length > 0 && (
                <Reveal delay={0.16} className="mt-10">
                  <span className="eyebrow block">Scope of Work</span>
                  <ul className="mt-5" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {project.scope.map((item) => (
                      <li
                        key={item}
                        className="py-[10px] text-[14px]"
                        style={{ borderTop: "1px solid rgba(23,21,34,0.08)", color: "rgba(23,21,34,0.78)" }}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="lk-section-tight" data-testid="work-detail-related">
          <div className="lk-container-wide">
            <SectionLabel>More Work</SectionLabel>
            <div className="mt-10 grid gap-10 sm:grid-cols-3">
              {others.map((other) => (
                <Link key={other.slug} to={`/work/${other.slug}`} className="group block">
                  <div className="img-frame img-zoom" style={{ aspectRatio: "16 / 11" }}>
                    <img src={other.image} alt={other.title} loading="lazy" />
                  </div>
                  <span className="eyebrow mt-4 block">{other.category}</span>
                  <h3 className="mt-2 font-display text-[18px] font-extrabold tracking-[-0.02em]">{other.title}</h3>
                </Link>
              ))}
            </div>
            <div className="mt-12">
              <ArrowLink to="/work">View All Work</ArrowLink>
            </div>
          </div>
        </section>
      )}

      <ClosingCTA />
    </>
  );
}
